import { Flex, Label, Text, Link } from '@workwhile/ui'
import React from 'react'
import { useInvoiceConfigContextValue } from '../context/InvoiceConfigProvider'
import { ColumnDef } from '@tanstack/react-table'
import { capitalize } from 'lodash'
import { PaginatedTable } from 'components/Table/PaginatedTable'
import { DateWithRelativeTime } from 'components/Table/DateWithRelativeTime'
import { InvoiceConfigRow } from '../types'
import { ActionsCell } from './ActionsCell'

const columns: ColumnDef<InvoiceConfigRow>[] = [
  {
    header: 'Owner',
    accessorKey: 'owner',
    enableResizing: true,
    minSize: 180,
    cell: ({ row }) => {
      const { company, companyGroup } = row.original
      if (company != null) {
        return (
          <Flex alignItems="center" gap={2} style={{ whiteSpace: 'nowrap' }}>
            <Label variant="information" size="small" inline>
              Company
            </Label>
            <Text>
              {company.name} (
              <Link to={`/companies/${company.id}`}>{company.id}</Link>)
            </Text>
          </Flex>
        )
      }
      if (companyGroup != null) {
        return (
          <Flex alignItems="center" gap={2} style={{ whiteSpace: 'nowrap' }}>
            <Label variant="neutral" size="small" inline>
              Company Group
            </Label>
            <Text>
              {companyGroup.name} (
              <Link to={`/company-groups/${companyGroup.id}`}>
                {companyGroup.id}
              </Link>
              )
            </Text>
          </Flex>
        )
      }
      return <Text color="lightText">-</Text>
    },
  },
  {
    header: 'Breakdown Levels',
    accessorKey: 'breakdownLevels',
    enableResizing: true,
    minSize: 180,
    cell: ({ row }) => {
      const { breakdownLevels } = row.original
      if (breakdownLevels.length === 0) {
        return <Text color="lightText">-</Text>
      }
      return <Text>{breakdownLevels.map(capitalize).join(', ')}</Text>
    },
  },
  {
    header: 'Custom Week',
    accessorKey: 'customWeek',
    enableResizing: true,
    minSize: 180,
    cell: ({ row }) => {
      const { customWeek } = row.original
      if (customWeek == null) {
        return <Text color="lightText">-</Text>
      }
      return (
        <Text>
          {capitalize(customWeek.weekStartDay)} start ({customWeek.timezone})
        </Text>
      )
    },
  },
  {
    header: 'Created At',
    accessorKey: 'createdAt',
    enableResizing: true,
    size: 160,
    cell: ({ row }) => <DateWithRelativeTime date={row.original.createdAt} />,
  },
  {
    header: 'Updated At',
    accessorKey: 'updatedAt',
    enableResizing: true,
    size: 160,
    cell: ({ row }) => <DateWithRelativeTime date={row.original.updatedAt} />,
  },
  {
    header: 'Actions',
    accessorKey: 'actions',
    size: 70,
    cell: ({ row }) => <ActionsCell invoiceConfig={row.original} />,
  },
]

export const InvoiceConfigTable = () => {
  const {
    state: { data, pagination, isFetching },
    actions: { setPagination },
  } = useInvoiceConfigContextValue()

  return (
    <PaginatedTable
      columns={columns}
      items={data.items}
      rowCount={data.totalRows}
      pagination={pagination}
      setPagination={setPagination}
      isFetching={isFetching}
      emptyMessage="No invoice configs found."
      emptyStateMaxWidth={['100%', '100%', '50%', '50%']}
    />
  )
}
